import { useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';

import {
  EditableUser,
  EditableAccount,
  AccountUse,
  accountUseIsOrg,
} from 'api-bible-shared-lib/utils/types';
import {
  accountUseOtherValidator,
  accountUseValidator,
  nameValidator,
  organizationNameValidator,
} from 'api-bible-shared-lib/utils/validation';

import FormHeader from './FormHeader';
import { Button } from './Button';
import { InputGroup, InputSelect } from './Inputs';
import { IconDownChevron } from './Icons';
import { InputCheckboxTermsAndConditions } from './TermsAndConditionsModal';

import { useAccountUser } from '../contexts/User';

export interface AccountFormState {
  user: EditableUser;
  account: EditableAccount;
}

interface AccountFormProps {
  handleSubmit: (accountFormData: AccountFormState) => Promise<void>;
  isRegistration?: boolean;
}

export function AccountForm({ handleSubmit, isRegistration = false }: AccountFormProps) {
  const { user, account, acceptedLatestTermsAndConditions } = useAccountUser();

  const initialValues = useMemo<AccountFormState | undefined>(() => {
    if (!user || !account) return undefined;

    return {
      user: {
        name: user.name || '',
      },
      account: {
        use: account.use || '',
        useOther: account.useOther || '',
        name: account.name || '',
        website: account.website || '',
        acceptedTermsAndConditionsVersion: '',
      },
    };
  }, [user, account]);

  if (!user || !account || !initialValues) return null;

  const validationSchema = yup.object().shape({
    user: yup.object().shape({
      name: nameValidator.clone(),
    }),
    account: yup.object().shape({
      use: accountUseValidator.clone(),
      useOther: accountUseOtherValidator.clone(),
      name: organizationNameValidator.clone(),
      website: yup.string().url(),
      acceptedTermsAndConditionsVersion: yup.string().when([], {
        is: () => !acceptedLatestTermsAndConditions,
        then: yup.string().required('Required'),
      }),
    }),
  });

  const onSubmit = async (values: AccountFormState, { setSubmitting, setFieldValue }: any) => {
    if (!accountUseIsOrg(values.account.use as AccountUse)) {
      setFieldValue('account.name', '');
      setFieldValue('account.website', '');
    }

    if (values.account.use !== AccountUse.other) {
      setFieldValue('account.useOther', '');
    }

    await handleSubmit({ ...values });
    setSubmitting(false);
  };

  if (!account) return null;

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ isSubmitting, values }) => (
        <Form>
          <FormHeader />
          {isRegistration && (
            <InputGroup name="user.name" label="Full Name" placeholder="Your full name" />
          )}

          <InputSelect name="account.use" label="Account Use" placeholder="Your intended use">
            {Object.values(AccountUse).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </InputSelect>

          {values.account.use === AccountUse.other && (
            <InputGroup name="account.useOther" placeholder="Describe your intended use" />
          )}
          {accountUseIsOrg(values.account.use as AccountUse) && (
            <>
              <InputGroup
                name="account.name"
                label="Account Name"
                placeholder="Name of your company or organization"
              />
              <InputGroup
                name="account.website"
                label="Website"
                placeholder="https://example.com"
              />
            </>
          )}

          {!isRegistration &&
            !acceptedLatestTermsAndConditions &&
            !values.account.acceptedTermsAndConditionsVersion && (
              <div className="text-sm text-red-500">
                Our Terms and Conditions have changed since you last accepted them. Please confirm
                again.
              </div>
            )}
          {!acceptedLatestTermsAndConditions && (
            <InputCheckboxTermsAndConditions
              name="account.acceptedTermsAndConditionsVersion"
              value={values.account.acceptedTermsAndConditionsVersion}
            />
          )}

          <div className="flex justify-end">
            <Button outline disabled={isSubmitting} loading={isSubmitting} type="submit">
              {isRegistration ? (
                <>
                  Create Application
                  <IconDownChevron className="w-6 h-6 transform -rotate-90" />
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
